import Box from "@mui/joy/Box";
import { CssVarsProvider } from "@mui/joy/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";
import { Navigate, Outlet, matchPath, useLocation } from "react-router-dom";
import background from "./shared/assets/bg-global.png";
import {
  AccountLifecycleQuartzClientContext,
  AdminQuartzClientContext,
  AnalyticsClientContext,
  AppointmentQuartzClientContext,
  CohortQuartzClientContext,
  DocumentQuartzClientContext,
  NoteQuartzClientContext,
  OmopQuartzClientContext,
  PersonQuartzClientContext,
  QuestionAnsweringClientContext,
  RPMQuartzClientContext,
  StudyQuartzClientContext,
  TaskQuartzClientContext,
  TerminologyQuartzClientContext,
  UserInfoProviderContext,
} from "./shared/services/api/contexts";
import { SnackbarProvider } from "./shared/components/layout/SnackbarContext";
import { initialize } from "./shared/constants/config";
import { BundleId } from "./models/bundle";
import PageLayout from "./features/auth/PageLayout";
import theme from "./shared/constants/theme";
import CssBaseline from "@mui/joy/CssBaseline";
import { QuartzClient } from "./shared/services/api/quartz";
import { UserProvider } from "./features/admin/providers/UserProvider";

initialize();

type BundleContextType = [BundleId | null, React.Dispatch<React.SetStateAction<BundleId | null>>];
export const BundleContext = React.createContext<BundleContextType>(
  null as unknown as BundleContextType
);

export default function App() {
  const auth = React.useContext(UserInfoProviderContext);
  const [bundleId, setBundleId] = React.useState<BundleId | null>(null);

  const location = useLocation();
  let noLayout = false;

  const match_note = matchPath(
    {
      path: "/note/:id",
    },
    location.pathname
  );
  if (match_note) noLayout = true;

  const quartzClientRef = React.useRef(
    new QuartzClient(window.CONFIG.backendUrl, () => {
      if (window.DISABLE_AUTH) {
        return Promise.resolve(null);
      } else {
        return auth.getAccessToken().then((token) => `Bearer ${token}`);
      }
    })
  );

  if (!window.DISABLE_AUTH && !auth.isAuthenticated()) {
    return <Navigate to="/auth/signin" />;
  }

  return (
    <CssVarsProvider theme={theme}>
      <CssBaseline />
      <Box
        sx={{
          position: "fixed",
          zIndex: -1,
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundImage: `url(${background})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          overflow: "auto",
        }}
      >
        <SnackbarProvider>
          <AccountLifecycleQuartzClientContext.Provider
            value={quartzClientRef.current.accountLifecycle}
          >
            <AnalyticsClientContext.Provider value={quartzClientRef.current.analytics}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <TerminologyQuartzClientContext.Provider
                  value={quartzClientRef.current.terminology}
                >
                  <UserProvider>
                    <TaskQuartzClientContext.Provider value={quartzClientRef.current.task}>
                      <BundleContext.Provider value={[bundleId, setBundleId]}>
                        <NoteQuartzClientContext.Provider value={quartzClientRef.current.note}>
                          <OmopQuartzClientContext.Provider value={quartzClientRef.current.omop}>
                            <StudyQuartzClientContext.Provider
                              value={quartzClientRef.current.study}
                            >
                              <CohortQuartzClientContext.Provider
                                value={quartzClientRef.current.cohort}
                              >
                                <AdminQuartzClientContext.Provider
                                  value={quartzClientRef.current.admin}
                                >
                                  <PersonQuartzClientContext.Provider
                                    value={quartzClientRef.current.person}
                                  >
                                    <RPMQuartzClientContext.Provider
                                      value={quartzClientRef.current.rpm}
                                    >
                                      <AppointmentQuartzClientContext.Provider
                                        value={quartzClientRef.current.appointment}
                                      >
                                        <QuestionAnsweringClientContext.Provider
                                          value={quartzClientRef.current.questionAnswering}
                                        >
                                          <DocumentQuartzClientContext.Provider
                                            value={quartzClientRef.current.document}
                                          >
                                            <PageLayout noLayout={noLayout}>
                                              <Outlet />
                                            </PageLayout>
                                          </DocumentQuartzClientContext.Provider>
                                        </QuestionAnsweringClientContext.Provider>
                                      </AppointmentQuartzClientContext.Provider>
                                    </RPMQuartzClientContext.Provider>
                                  </PersonQuartzClientContext.Provider>
                                </AdminQuartzClientContext.Provider>
                              </CohortQuartzClientContext.Provider>
                            </StudyQuartzClientContext.Provider>
                          </OmopQuartzClientContext.Provider>
                        </NoteQuartzClientContext.Provider>
                      </BundleContext.Provider>
                    </TaskQuartzClientContext.Provider>
                  </UserProvider>
                </TerminologyQuartzClientContext.Provider>
              </LocalizationProvider>
            </AnalyticsClientContext.Provider>
          </AccountLifecycleQuartzClientContext.Provider>
        </SnackbarProvider>
      </Box>
    </CssVarsProvider>
  );
}
